import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Eye, FileText, Search, Zap } from 'lucide-react';

const SupplyChain = () => {
  // eslint-disable-next-line no-unused-vars
  const [suppliers, setSuppliers] = useState([
    { id: 1, name: "Eco Packaging Co.", rating: "A", risk: "Low", industry: "Packaging", lastAudit: "2023-05-15" },
    { id: 2, name: "Green Energy Ltd.", rating: "B", risk: "Medium", industry: "Energy", lastAudit: "2023-03-22" },
    { id: 3, name: "Sustainable Materials Inc.", rating: "A-", risk: "Low", industry: "Manufacturing", lastAudit: "2023-06-01" },
    { id: 4, name: "Global Logistics Corp.", rating: "C", risk: "High", industry: "Transportation", lastAudit: "2023-02-10" },
  ]);

  const [searchTerm, setSearchTerm] = useState('');

  const getRiskColor = (risk) => {
    switch(risk) {
      case 'Low': return 'bg-green-100 text-green-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'High': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredSuppliers = suppliers.filter(supplier =>
    supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.industry.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const riskDistribution = suppliers.reduce((acc, supplier) => {
    acc[supplier.risk] = (acc[supplier.risk] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold mb-6">Supply Chain Sustainability</h1>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Supplier Risk Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-around">
            {Object.entries(riskDistribution).map(([risk, count]) => (
              <div key={risk} className="text-center">
                <Badge className={getRiskColor(risk)}>{risk}</Badge>
                <p className="mt-2 font-bold">{count} Supplier{count !== 1 ? 's' : ''}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="mb-4 flex items-center">
        <Search className="mr-2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search suppliers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-grow"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredSuppliers.map((supplier) => (
          <Card key={supplier.id}>
            <CardHeader>
              <CardTitle className="flex justify-between items-center">
                {supplier.name}
                <Badge variant="outline">{supplier.industry}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <p className="font-semibold">ESG Rating: <span className="text-blue-600">{supplier.rating}</span></p>
                  <p>Last Audit: {supplier.lastAudit}</p>
                </div>
                <Badge className={getRiskColor(supplier.risk)}>{supplier.risk} Risk</Badge>
              </div>
              <div className="flex space-x-2">
                <Button variant="outline" size="sm"><Eye className="mr-2 h-4 w-4" /> View Details</Button>
                <Button variant="outline" size="sm"><FileText className="mr-2 h-4 w-4" /> Audit Report</Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Zap className="mr-2 h-5 w-5 text-yellow-500" />
            AI-Generated Insights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="list-disc pl-5 space-y-2">
            <li>75% of your suppliers have a low or medium risk rating, indicating a generally stable supply chain.</li>
            <li>Global Logistics Corp. has a high risk rating. Consider scheduling an earlier audit and developing an improvement plan.</li>
            <li>The packaging industry shows the best sustainability performance. Consider sharing best practices from Eco Packaging Co. with other suppliers.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default SupplyChain;